import { createFormatForDescription as cffd } from '../createFormatForDescription'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import EscrowCreateInstructions from '../types/instructions/EscrowCreateInstructions'
import TransactionType from '../types/TransactionType'
import { parseDate } from '../util'

/**
 * An annotation for EscrowCreate transactions.
 *
 * @property type - The type of the transaction.
 * @property amount - An explanation of the amount.
 * @property cancelAfter - An explanation of the cancelAfter.
 * @property condition - An explanation of the condition.
 * @property destination - An explanation of the destination.
 * @property destinationTag - An explanation of the destinationTag.
 * @property finishAfter - An explanation of the finishAfter.
 */
export interface EscrowCreateAnnotation {
  readonly type: TransactionType.EscrowCreate
  readonly amount: Explanation<string>
  readonly cancelAfter?: Explanation<number>
  readonly condition?: Explanation<string>
  readonly destination: Explanation<string>
  readonly destinationTag?: Explanation<string>
  readonly finishAfter?: Explanation<number>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: EscrowCreateInstructions,
): EscrowCreateAnnotation {
  const amount = explain(
    transactionInstructions.Amount,
    `${cffd(
      transactionInstructions.Amount,
      'amount',
    )} XRP (in drops) will be escrowed.`,
  )

  const condition = explainOptional(
    transactionInstructions.Condition,
    (theCondition) =>
      `${cffd(
        theCondition,
        'condition',
      )} is a hex value representing a PREIMAGE-SHA-256 crypto-condition. The funds can only be delivered to the recipient if this condition is fulfilled.`,
  )

  const destination = explain(
    transactionInstructions.Destination,
    `Address ${cffd(
      transactionInstructions.Destination,
      'destination',
    )} will receive the funds.`,
  )

  const destinationTag = explainOptional(
    transactionInstructions.DestinationTag,
    (tag) =>
      `This tag identifies a hosted recipient or other information for the recipient of the deleted account's leftover XRP: ${cffd(
        tag,
        'destinationTag',
      )}`,
  )

  const cancelAfter = explainOptional(
    transactionInstructions.CancelAfter,
    (date) =>
      `This escrow will be cancelled on ${cffd(
        parseDate(date),
        'cancelAfter',
      )}. Afterwards, funds can only be returned to the sender.`,
  )

  const finishAfter = explainOptional(
    transactionInstructions.FinishAfter,
    (date) =>
      `The recipient cannot receive the funds until ${cffd(
        parseDate(date),
        'finishAfter',
      )}.`,
  )

  return {
    amount,
    ...(cancelAfter && { cancelAfter }),
    ...(condition && { condition }),
    destination,
    ...(destinationTag && { destinationTag }),
    ...(finishAfter && { finishAfter }),

    type: TransactionType.EscrowCreate,
  }
}
